import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Tabs, TabList, Tab, TabPanel, isReactComponent } from '@panwds/react-ui'
import { history, getAuthState } from '@sparky/framework'
import type { ReactElement, ComponentType, ReactNode, VFC } from 'react'
import type { Location as HistoryLocation } from 'history'
import type { NavItem, IndexedArray } from '@sparky/framework'
import type { TabProps } from '@panwds/react-ui'
import { getVarValue } from '../common/utils/Sparky'
import { FF_DEFAULT_TSG } from '../common/utils/FeatureFlags'

const enableDefaultTSG = getVarValue(FF_DEFAULT_TSG)

export type HeaderProps = {
  navItem: NavItem
  title?: string
  subtitle?: string
  dataTestId?: string
}
export type TabPanelWrapperProps = {
  navItem: NavItem
  children: ReactNode | undefined
  panelProps: any
}

export type TabLayoutProps = {
  navItem: NavItem
  location?: HistoryLocation | Location
  title?: string
  subtitle?: string
  rememberLastTab?: boolean
  tabSize?: TabProps['size']
  dataTestId?: string
  dataMetrics?: string
  panelProps: any
  header?: ReactNode
  TabPanelWrapper?: ComponentType<TabPanelWrapperProps>
}

type RenderedTab = {
  id: string
  path: string
  key: string
  tab: ReactElement
  original: NavItem
}

type TabPanelProps = Record<string, any> | undefined
type TabPanelRenderer = (props?: TabPanelProps) => ReactNode

type NavItemState = {
  id: string
  path: string
}
type NormalizedNavItem = NavItem & NavItemState
type IndexedChildren = IndexedArray<NormalizedNavItem> & {
  getPathByTabId: (id: string) => string | undefined
}

const useNavItemState = (navItem: NavItem): NavItemState => {
  // temp store the last selected tab in the navItem, should move to preference in future
  const stateRef = useRef<NavItemState>({ id: '', path: '' })
  const navItemChildrenState = useMemo(() => {
    const childrenAny = navItem.children as any
    if (!childrenAny?.length) {
      return stateRef.current
    }
    stateRef.current = childrenAny.state || (childrenAny.state = stateRef.current)
    return stateRef.current
  }, [navItem])
  return navItemChildrenState
}

const TabLayout: VFC<TabLayoutProps> = ({
  navItem,
  header,
  TabPanelWrapper,
  location = window.location,
  tabSize,
  panelProps,
  rememberLastTab = true,
  dataTestId = `tabs-${navItem.path || navItem.key}`,
  dataMetrics,
  // ...passdownProps
}) => {
  const locPath = location.pathname
  const navItemState = useNavItemState(navItem)

  const indexedChildren = useMemo(() => {
    if (!navItem.children?.length) {
      return undefined
    }
    const idPathMap = new Map()
    const pathChildMap = new Map()
    const filtered = navItem.children.map(item => {
      const path = item.path || `${navItem.path}/${item.key}`
      const id = path.replace(/\W/g, '_')
      const key = item.key || id
      const copied = {
        ...item,
        original: item,
        id,
        key,
        path,
      } as NormalizedNavItem
      idPathMap.set(id, path)
      pathChildMap.set(path, copied)
      if (Array.isArray(item.children)) {
        for (const child of item.children) {
          if (child.path) {
            pathChildMap.set(child.path, copied)
          }
        }
      }
      return copied
    }).filter(Boolean) as NormalizedNavItem[]
    if (!filtered.length) {
      return undefined
    }
    const indexedChildren = Object.assign(filtered, {
      getPathByTabId: idPathMap.get.bind(idPathMap),
      get: pathChildMap.get.bind(pathChildMap),
      has: pathChildMap.has.bind(pathChildMap),
    }) as IndexedChildren
    return indexedChildren
  }, [navItem])

  const locNavItem = indexedChildren?.get(locPath)
  if (locNavItem) {
    navItemState.id = locNavItem.id
    navItemState.path = locNavItem.path
  }

  const [defaultTab, tabs] = useMemo(() => {
    const tabs = [] as RenderedTab[]
    if (!indexedChildren) {
      return [{ id: '', path: '' } as RenderedTab, tabs]
    }
    for (const child of indexedChildren) {
      const {
        id,
        key,
        title,
        // component: Component,
        path,
      } = child
      const tab = (
        <Tab key={key} id={id} data-path={path} dataTestId={`tab-${id}`} size={tabSize}>
          {title}
        </Tab>
      )
      tabs.push({
        id,
        key,
        path: path!,
        tab,
        original: child.original,
      })
    }
    return [tabs[0], tabs]
  }, [indexedChildren, tabSize])

  const setActivateTabId = useCallback((tabId: string) => {
    const path = indexedChildren?.getPathByTabId(tabId) || defaultTab.path
    // console.info('try puth', tabId, path, history.location.pathname !== path)
    if (path && history.location.pathname !== path) {
      const locNavItem = indexedChildren?.get(path)
      if (locNavItem) {
        navItemState.id = locNavItem.id
        navItemState.path = locNavItem.path
      }
      history.push(path)
    }
  }, [defaultTab, indexedChildren, navItemState])

  const [tabList, renderTabPanels] = useMemo(() => {
    const tabBtns = []
    const tabPanels = [] as TabPanelRenderer[]
    for (const { id, key, tab, original } of tabs) {
      const Comp = original.component as VFC<any>
      tabBtns.push(tab)
      tabPanels.push((props?: TabPanelProps) => (
        <TabPanel key={key} activeTabId={navItemState.id} forTabId={id} dataTestId={`tab-panel-${id}`} addClassName='cm-m-0 cm-flex cm-flex-col cm-flex-1 tw-my-0 tw-ml-0'>
          <Comp {...panelProps} {...props} navItem={original} />
        </TabPanel>
      ))
    }
    // console.error('render memo', navItemState.id)
    const tabList = (
      <TabList
        activeTabId={navItemState.id}
        onActiveChange={setActivateTabId}
      >{tabBtns}</TabList>
    )
    const renderer: TabPanelRenderer = (props) => <>{tabPanels.map(render => render(props))}</>
    return [tabList, renderer]
  }, [navItemState.id, setActivateTabId, tabs, panelProps])

  useEffect(() => { // default redirect to default tab
    if (locPath === navItem.path) {
      const curPath = (rememberLastTab && navItemState.path) || defaultTab.path
      if (curPath && curPath !== locPath) {
        history.replace(curPath)
      }
    }
  }, [defaultTab, locPath, navItem.path, navItemState.path, rememberLastTab])

  const showSideBar = useMemo(() => {
    const authState = getAuthState()
    if (enableDefaultTSG && !authState?.tsgs?.hasMsp) {
      return false
    }
    if (!locNavItem) {
      return false
    }
    if (locNavItem.path === locPath) {
      return Boolean(locNavItem.showSideBar)
    }
    const child = locNavItem.children?.find(child => child.path === locPath)
    if (child) {
      return Boolean(child.showSideBar ?? locNavItem.showSideBar)
    }
    return false
  }, [locNavItem, locPath])

  const tabPanelsWithWrapper = TabPanelWrapper && isReactComponent(TabPanelWrapper) ? (
    <TabPanelWrapper navItem={navItem} panelProps={{ ...panelProps, showSideBar }}>{renderTabPanels}</TabPanelWrapper>
  ) : renderTabPanels()

  return <Tabs dataTestId={dataTestId} dataMetrics={dataMetrics}>
    <div className='panwds-tw3 cm-bg-gray-100 dark:cm-bg-gray-1050'>
      {header}
      {tabList}
    </div>
    {tabPanelsWithWrapper}
  </Tabs>
}

Object.assign(TabLayout, {
  lazy: false,
  layout: 'Tabs',
})

export default TabLayout
